import { Injectable, OnDestroy } from '@angular/core';
import { cloneDeep } from 'lodash';

import { untilDestroyed } from '@app/core';
import {
  CustomSignerInput,
  DeliveryMethodEnum,
  Document,
  DocumentInput,
  FloatingField,
  SecurityVerificationEnum,
  SignerInput,
  SignerMainAttributes,
  User,
  FallbackBehaviorEnum,
  SignerTypeEnum
} from '@app/models';
import { UserService } from '@app/services';
import { UniqueSignersPipe } from './unique-signers.pipe';

@Injectable({ providedIn: 'root' })
export class DocumentFormService implements OnDestroy {
  private currentUser: User;

  constructor(
    private userService: UserService,
    private uniqueSigners: UniqueSignersPipe
  ) {
    this.userService
      .watchCurrentUser()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: user => (this.currentUser = user)
      });
  }

  ngOnDestroy() {}

  transformToSignerInputs(document: DocumentInput | Document, customSigners: CustomSignerInput[], floatingFields: FloatingField[], isMarkdown: boolean = false): SignerInput[] {
    const customSignersClone = this.uniqueSigners.transform(cloneDeep(customSigners || []), document.qualified);
    customSignersClone.forEach(signer => {
      if (signer && signer._extra) {
        if (!signer._extra.showCpfVerification) {
          delete signer.configs?.cpf;
          delete signer.configs?.cnpj;
        }

        signer.security_verifications = [];
        if (signer._extra.showSmsVerification && [SignerMainAttributes.Email, SignerMainAttributes.Name].includes(signer._extra.signerMainAttribute)) {
          signer.security_verifications.push({
            type: SecurityVerificationEnum.Sms,
            verify_phone: signer._extra.phoneNumber || null
          });
        }
        if (signer._extra.showSecurityVerifications && signer._extra.documentType) {
          signer.security_verifications.push({
            ...(signer._extra.fallbackEnabled ? {} : { fallback_behavior: FallbackBehaviorEnum.DisableFallback }),
            type: signer._extra.documentType as SecurityVerificationEnum
          });
        }
      }
      if (signer && signer._extra && signer._extra.signerMainAttribute && signer[signer._extra.signerMainAttribute]) {
        signer[signer._extra.signerMainAttribute] = signer[signer._extra.signerMainAttribute].trim();
      }
      const signerIdentification = this.uniqueSigners.identification(signer);
      signer.positions = (floatingFields || [])
        .filter(field => field.identification === signerIdentification)
        .map(field => ({ element: field.element, x: String(field.position.x), y: String(field.position.y), z: field.position.z, angle: field.position.angle, scale: field.position.scale || 1 }));

      if ((signer.positions || []).length === 0) {
        delete signer.positions;
      }

      if ([SignerMainAttributes.Sms, SignerMainAttributes.Whatsapp].includes(signer._extra.signerMainAttribute)) {
        delete signer.name;
        delete signer.email;

        signer.phone = signer?.sms || signer?.whatsapp;

        delete signer.sms;
        delete signer.whatsapp;
      }

      if ([SignerMainAttributes.Email].includes(signer._extra.signerMainAttribute)) signer.delivery_method = DeliveryMethodEnum.DeliveryMethodEmail;
      if ([SignerMainAttributes.Name].includes(signer._extra.signerMainAttribute)) signer.delivery_method = DeliveryMethodEnum.DeliveryMethodLink;
      if ([SignerMainAttributes.Sms].includes(signer._extra.signerMainAttribute)) signer.delivery_method = DeliveryMethodEnum.DeliveryMethodSms;
      if ([SignerMainAttributes.Whatsapp].includes(signer._extra.signerMainAttribute)) {
        signer.delivery_method = DeliveryMethodEnum.DeliveryMethodWhatsapp;
        if (isMarkdown) {
          delete signer.configs?.cnpj;
          if (!signer._extra.showCpfVerification) {
            delete signer.configs?.cpf;
          }
          delete signer.security_verifications;
        }
      }
      if (signer?.configs && document.locale.country !== 'BR') {
        delete signer.configs?.cpf;
        delete signer.configs?.cnpj;
      }
    });

    return customSignersClone.map(signer => {
      if (signer._extra.isQualified) signer.type = SignerTypeEnum.Qualified;
      delete signer._extra;
      if (document.qualified || (signer.security_verifications || []).length === 0) delete signer.security_verifications;
      if (Object.keys(signer?.configs || {}).length === 0) delete signer.configs;
      return signer;
    });
  }

  transformToSignerInputsTemplate(document: DocumentInput | Document, signer: CustomSignerInput): SignerInput {
    if (signer && signer._extra) {
      if (!signer._extra.showCpfVerification || document.qualified) {
        (signer.configs || {}).cpf = null;
      }
      signer.security_verifications = [];
      if (signer._extra.showSmsVerification && [SignerMainAttributes.Email, SignerMainAttributes.Name].includes(signer._extra.signerMainAttribute)) {
        signer.security_verifications.push({
          type: SecurityVerificationEnum.Sms,
          ...(signer._extra.phoneNumber ? { verify_phone: signer._extra.phoneNumber } : {})
        });
      }
      if (signer._extra.showSecurityVerifications && signer._extra.documentType) {
        signer.security_verifications.push({
          ...{ fallback_behavior: signer._extra.fallbackEnabled ? null : FallbackBehaviorEnum.DisableFallback },
          type: signer._extra.documentType as SecurityVerificationEnum
        });
      }

      if (signer._extra.signerMainAttribute && signer[signer._extra.signerMainAttribute]) {
        signer[signer._extra.signerMainAttribute] = signer[signer._extra.signerMainAttribute].trim();
      }
    }

    if ((signer.positions || []).length === 0) {
      delete signer.positions;
    }

    if (signer._extra && signer._extra.signerMainAttribute && [SignerMainAttributes.Sms, SignerMainAttributes.Whatsapp].includes(signer._extra.signerMainAttribute)) {
      delete signer.name;
      delete signer.email;

      signer.phone = signer?.sms || signer?.whatsapp;

      delete signer.sms;
      delete signer.whatsapp;
    }

    if (signer._extra && signer._extra.signerMainAttribute) {
      const attributeMap = {
        [SignerMainAttributes.Email]: DeliveryMethodEnum.DeliveryMethodEmail,
        [SignerMainAttributes.Name]: DeliveryMethodEnum.DeliveryMethodLink,
        [SignerMainAttributes.Sms]: DeliveryMethodEnum.DeliveryMethodSms,
        [SignerMainAttributes.Whatsapp]: DeliveryMethodEnum.DeliveryMethodWhatsapp
      };
      signer.delivery_method = attributeMap[signer._extra.signerMainAttribute as keyof typeof attributeMap];
    }
    if (signer?.configs?.cpf && document.locale.country !== 'BR') delete signer.configs.cpf;

    signer.type = signer._extra?.isQualified ? SignerTypeEnum.Qualified : SignerTypeEnum.Standard;
    delete signer._extra;
    if (!signer.security_verifications || document.qualified) delete signer.security_verifications;
    return signer;
  }
}
